// Stable visitor identity (UUID in localStorage, no PII) drives small visual
// offsets so two visitors on the same day see meaningfully different palettes
// and the same visitor sees coherent identity day-to-day. Daily cadence means
// without this lever the page looks frozen between :00 ticks.

const STORAGE_KEY = 'herminator:visitor-id';

function makeUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older environments — sufficient for non-cryptographic seed use
  const r = (): string => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${r()}${r()}-${r()}-${r()}-${r()}-${r()}${r()}${r()}`;
}

export function getStableVisitorId(): string {
  if (typeof localStorage === 'undefined') return 'transient';
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = makeUuid();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    return 'transient';
  }
}

// 32-bit FNV-1a — fast, well-distributed, no crypto needed for visual seeds
function fnv1a(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface VisitorSeed {
  hueOffsetDeg: number;       // ±15°
  particlePhase: number;      // 0..1
  noiseSeed: number;          // 0..1
  raw: number;                // 32-bit
}

export function deriveVisitorSeed(visitorId: string, cycle: number): VisitorSeed {
  const raw = fnv1a(`${visitorId}::${cycle}`);
  const a = (raw & 0xffff) / 0xffff;
  const b = ((raw >>> 16) & 0xffff) / 0xffff;
  return {
    hueOffsetDeg: (a * 30) - 15,
    particlePhase: b,
    noiseSeed: ((raw >>> 8) & 0xffff) / 0xffff,
    raw,
  };
}
